import {
  DataType,
  Form,
  FormOptions,
  FormSkeleton,
  Section,
  SelectOption,
  useNotify
} from '@globaleris/ui.library'
import { Box, Breadcrumbs, Paper, Typography, makeStyles, useTheme } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { CommercialModel } from '../../../api/Argentia/model/commercials/commercial'
import { ContractProductAPIModel } from '../../../api/Pioneer/model/Contracts'
import Page, { PageLayout } from '../../../components/page/page'
import PageHeader from '../../../components/page/pageHeader'
import { BreadcrumbLink } from '../../../components/shared/breadcrumbLink/breadcrumbLink'
import { useConfirmationDialog } from '../../../hooks'
import { GlobalNotification, NonAuthRoutes } from '../../../model'
import {
  fetchClientCommercialsByID,
  fetchClientDataByID,
  getCustomerDomain,
  notifyErrorAction
} from '../../../redux'
import {
  selectClientCommercials,
  selectClientData,
  selectIsFetchingClientData,
  selectIsFetchingClientDataError
} from '../../../redux/selectors/client.selectors'
import { getLinkClientText } from '../../clients/client.helper'
import { SubjectTableModel } from '../../clients/clients.model'
import { RulebookContractsValidations } from '../../contracts/contracts.model'
import {
  fetchContractsBySubjectID,
  fetchProductsByContractID
} from '../../contracts/contracts.service'
import { styles } from '../../regulatoryReports'
import {
  commercialFormModelToPostRequestMapper,
  commercialFormModelToUpdateRequestMapper,
  commercialModelListToFormModelMapper,
  commercialModelToFormModelMapper,
  contractProductsListMapper,
  contractsListMapper,
  productFromcontractProductsListMapper,
  subjectCommercialsItemMapper
} from './commercials.mapper'
import { CommercialsFormValidations } from './commercials.model'
import {
  createCommercial,
  fetchCommercialByID,
  fetchCommercialSubjects,
  fetchContractProductsBySubjectID,
  updateCommercial
} from './commercials.service'
import { commercialsToTableModelMapper } from '../../clients/client.mapper'
import { EnumCommercialRelationActionType } from '../../../api/oData/enums/enumCommercialRelationActionType'
import { ContractModel } from '../../../api/Argentia/model/contracts'
import { enumEnumToValueNumber } from '../../../helpers'
import { validateForContract, validateForProduct } from './commercials.helpers'

export const initialValidations: CommercialsFormValidations = {
  isDirty: false
}

interface ContractAddPageProps {
  location: {
    state: { clientID: number; commercialID?: number; pathName?: NonAuthRoutes }
  }
}

export interface CommercialFormModel extends CommercialModel {
  linkToAllContracts: boolean
  linkToAllProducts: boolean
}

export const CommercialDetailsPage: React.FC<ContractAddPageProps> = props => {
  const history = useHistory()
  const [t] = useTranslation(['commercials', 'globalerisLibrary', 'enums'])

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const [globalNotification, setGlobalNotification] = useState<GlobalNotification | undefined>(
    undefined
  )
  const { info } = useNotify()

  const clientData = useSelector(selectClientData)
  const commercials = commercialModelListToFormModelMapper(useSelector(selectClientCommercials))

  const isFetchingClient = useSelector(selectIsFetchingClientData)
  const isFetchingClientError = useSelector(selectIsFetchingClientDataError)

  const [commercial, setCommercial] = useState<CommercialFormModel>({
    linkToAllContracts: true,
    linkToAllProducts: true
  } as CommercialFormModel)

  const [isFetchingCommercial, setIsFetchingCommercial] = useState<boolean>(false)
  const [isFetchingDeps, setIsFetchingDeps] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [contractsListOptions, setContractsListOptions] = useState<SelectOption[]>([])
  const [missingContracts, setMissingContracts] = useState<boolean>(false)
  const [subjectProductsList, setSubjectProductsList] = useState<ContractProductAPIModel[]>([])
  const [subjectProductsFetching, setSubjectProductsFetching] = useState<boolean>(false)
  const [errorMsg, setErrorMsg] = useState<string>('')

  const [contractProductsList, setContractProductsList] = useState<ContractProductAPIModel[]>([
    { id: 999, productCode: t('commercials:forms.add.inputs.all') } as ContractProductAPIModel
  ])
  const [contractProductsFetching, setContractProductsFetching] = useState<boolean>(false)
  const [missingContractProducts, setMissingContractProducts] = useState<boolean>(false)
  const [linkAllProductsAllowed, setLinkAllProductsAllowed] = useState<boolean>(false)
  const [linkAllContractsAllowed, setLinkAllContractsAllowed] = useState<boolean>(false)

  const [commercialSubjects, setCommercialSubjects] = useState<SubjectTableModel[]>([])
  const [commercialSubjectsOptions, setCommercialSubjectsOptions] = useState<SelectOption[]>([])
  const [invalidCommSubject, setInvalidCommSubject] = useState<boolean>(false)

  const clientID = props.location.state.clientID
  const commercialID = props.location.state.commercialID

  // Hooks Region Starts
  useEffect(() => {
    if (!clientData?.id) {
      notifyErrorAction(t('contracts:forms.add.errors.clientNotFound'))
      history.push(NonAuthRoutes.Client)
    }

    if (commercialID) {
      getCommercial(commercialID)
    } else {
      validateActionsForEdit({} as CommercialFormModel)
    }

    const getCommercialSubjects = async () => {
      await fetchCommercialSubjects().then(response => {
        const commercialSubjects = response.data ?? []
        setCommercialSubjects(commercialSubjects)
        setCommercialSubjectsOptions(subjectCommercialsItemMapper(commercialSubjects))
      })
    }
    getCommercialSubjects()
  }, [])

  useEffect(() => {
    if (clientData?.id) {
      const getContracts = async () => {
        return await fetchContractsBySubjectID(clientData.id!).then(response => {
          if (response.data !== undefined) {
            return response.data
          }
          return []
        })
      }

      Promise.all([getContracts()]).then(([contracts]) => {
        contracts.length === 0 ? setMissingContracts(true) : setMissingContracts(false)
        contracts.length === 0 && setErrorMsg(t('commercials:forms.add.errors.missingContracts'))
        mapContractsOptions(contracts)
        setIsFetchingDeps(false)
      })
      setIsFetchingDeps(true)
    } else if (isFetchingClientError) {
      clientID && fetchClientDataByID(clientID)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientData])

  const mapContractsOptions = (contracts: ContractModel[]) => {
    let contractsToFilter: CommercialFormModel[] = []

    // Filter the contracts that are already linked to a commercial
    contractsToFilter = commercials.filter(
      comm =>
        comm.relationLevel === EnumCommercialRelationActionType.Contract &&
        (commercialID ? comm.id !== commercialID : true)
    )

    const filteredContracts = contracts.filter(
      c => !contractsToFilter.some(ctf => ctf.contractId === c.id)
    )

    setContractsListOptions(contractsListMapper(filteredContracts))
  }

  const mapContractProductsOptions = (
    contractID: number,
    contractProducts: ContractProductAPIModel[]
  ) => {
    let contractProductsToFilter: CommercialFormModel[] = []

    // Filter the contracts/products that are already linked to a commercial
    contractProductsToFilter = commercials.filter(
      comm =>
        comm.relationLevel === EnumCommercialRelationActionType.Product &&
        comm.contractId === contractID &&
        (commercialID ? comm.id !== commercialID : true)
    )

    // if (contractProductsToFilter.length > 0) {
    //   // TODO switch all products OFF + bloquear
    //   setLinkAllProductsAllowed(false)
    //   setCommercial(prevState => ({
    //     ...prevState,
    //     linkToAllProducts: false
    //     // contractProductId: undefined
    //   }))
    // } else {
    //   setLinkAllProductsAllowed(true)
    // }

    const filteredContractProducts = contractProducts.filter(
      c => !contractProductsToFilter.some(ctf => ctf.contractProductId === c.id)
    )

    filteredContractProducts.filter(p => p.id !== 999).length === 0
      ? setMissingContractProducts(true)
      : setMissingContractProducts(false)

    setContractProductsList(filteredContractProducts)
  }

  const validateActionsForEdit = (commercialItem: CommercialFormModel) => {
    let linkAllContractsAllowed = true
    let linkAllProductsAllowed = true

    if (
      commercials.some(
        c =>
          c.relationLevel === EnumCommercialRelationActionType.Subject &&
          (commercialID ? c.id !== commercialID : true)
      )
    ) {
      linkAllContractsAllowed = false
      linkAllProductsAllowed = false
      return
    }

    if (
      commercials.some(
        c =>
          c.relationLevel === EnumCommercialRelationActionType.Contract &&
          (commercialID ? c.id !== commercialID : true)
      )
    ) {
      linkAllContractsAllowed = false
    }

    if (
      commercials.some(
        c =>
          c.relationLevel === EnumCommercialRelationActionType.Product &&
          (commercialID ? c.id !== commercialID : true)
      )
    ) {
      linkAllContractsAllowed = false
    }

    const {
      allowLinkAllContracts: allowLinkAllContractsContractValidation,
      allowLinkAllProducts: allowLinkAllProductsContractValidation
    } = validateForContract(commercialItem, commercials, commercialID)

    const {
      allowLinkAllContracts: allowLinkAllContractsProductValidation,
      allowLinkAllProducts: allowLinkAllProductsProductValidation
    } = validateForProduct(commercialItem, commercials, commercialID)

    linkAllContractsAllowed =
      linkAllContractsAllowed &&
      allowLinkAllContractsContractValidation &&
      allowLinkAllContractsProductValidation
    linkAllProductsAllowed =
      linkAllProductsAllowed &&
      allowLinkAllProductsContractValidation &&
      allowLinkAllProductsProductValidation

    !linkAllContractsAllowed && setErrorMsg(t('commercials:forms.add.help.otherCommercials'))
    !linkAllProductsAllowed && setErrorMsg(t('commercials:forms.add.help.otherCommercials'))
    setLinkAllContractsAllowed(linkAllContractsAllowed)
    setLinkAllProductsAllowed(linkAllProductsAllowed)

    setCommercial(prevState => ({
      ...prevState,
      linkToAllContracts: linkAllContractsAllowed ? prevState.linkToAllContracts : false,
      linkToAllProducts: linkAllProductsAllowed ? prevState.linkToAllProducts : false,
      contractProductId:
        linkAllProductsAllowed && prevState.linkToAllProducts ? 999 : prevState.contractProductId
    }))
  }

  const getCommercial = async (id: number) => {
    setIsFetchingCommercial(true)
    await fetchCommercialByID(id).then(async response => {
      setIsFetchingCommercial(false)
      let commercial = response.data
      if (!commercial) {
        return
      }

      commercial?.contractId && getContractProducts(commercial.contractId)

      const commercialMapped = await mapCommercialProduct(
        commercialModelToFormModelMapper(commercial) as CommercialFormModel
      )

      setCommercial(prevState => ({ ...prevState, ...commercialMapped }))

      validateActionsForEdit(commercialMapped)
    })
  }

  const getContractProducts = async (contractID: number) => {
    setContractProductsFetching(true)
    await fetchProductsByContractID(contractID).then(async response => {
      setContractProductsFetching(false)
      let products: ContractProductAPIModel[] = []
      if (response.data !== undefined) {
        products = response.data
        if (products.length == 0) {
          setMissingContractProducts(true)
        } else {
          setMissingContractProducts(false)
        }
      }
      products.push({
        id: 999,
        productCode: t('commercials:forms.add.inputs.all')
      } as ContractProductAPIModel)
      mapContractProductsOptions(contractID, products)
    })
  }

  const getProductsOptions = async (
    commercialSubjectID: number
  ): Promise<ContractProductAPIModel[]> => {
    setSubjectProductsFetching(true)
    return await fetchContractProductsBySubjectID(commercialSubjectID).then(async response => {
      setSubjectProductsFetching(false)
      let products: ContractProductAPIModel[] = []
      if (response.data !== undefined) {
        products = response.data
        if (products.length == 0) {
          setInvalidCommSubject(true)
        } else {
          setInvalidCommSubject(false)
        }
      }

      setSubjectProductsList(products)
      return products
    })
  }

  const mapCommercialProduct = async (
    commercialItem: CommercialFormModel
  ): Promise<CommercialFormModel> => {
    let commercial = { ...commercialItem } as CommercialFormModel

    if (commercial?.commercialSubjectId && commercial?.productId) {
      const products = await getProductsOptions(commercial.commercialSubjectId)
      const product = products.find(p => p.id === commercial.productId)
      commercial = { ...commercial, productId: product ? product.productId : 0 }
    }

    return commercial
  }

  const saveCommercial = async (formData: CommercialFormModel): Promise<boolean> => {
    if (clientData?.id) {
      const contractProductId = formData.contractProductId

      const response = await createCommercial(
        commercialFormModelToPostRequestMapper({
          ...formData,
          contractProductId:
            contractProductId && contractProductId !== 999 ? contractProductId : undefined,
          subjectId: clientData.id
        })
      )
      if (response.success) {
        fetchClientCommercialsByID(clientData.id)
        info(t('commercials:forms.add.actions.save.success'))
        history.goBack()
      }
    }

    return true
  }

  const editCommercial = async (formData: CommercialFormModel): Promise<boolean> => {
    if (clientData?.id) {
      const contractProductId = formData.contractProductId
      const response = await updateCommercial(
        commercialFormModelToUpdateRequestMapper({
          ...formData,
          contractProductId:
            contractProductId && contractProductId !== 999 ? contractProductId : undefined,
          subjectId: clientData.id
        })
      )
      if (response.success) {
        fetchClientCommercialsByID(clientData.id)
        info(t('commercials:forms.add.actions.edit.success'))
        history.goBack()
      }
    }

    return true
  }

  const options: FormOptions = {
    columns: 3,
    analytics: {
      customer: getCustomerDomain(),
      view: t('commercials:forms.add.analytics')
    },
    saveDisabled:
      invalidCommSubject ||
      missingContractProducts ||
      (missingContracts && !commercial.linkToAllContracts) ||
      undefined,
    onSave: async data => {
      if (!data) {
        history.goBack()
      } else {
        return commercialID
          ? editCommercial(data as CommercialFormModel)
          : saveCommercial(data as CommercialFormModel)
      }
      return true
    }
  }

  const filterContractProductsOptions = () => {
    return commercial.linkToAllProducts
      ? contractProductsListMapper(contractProductsList)
      : contractProductsListMapper(contractProductsList).filter(option => option.value !== 999)
  }

  const sectionsData: Section[] = [
    {
      inputs: [
        {
          name: 'commercialSubjectId',
          label: t('commercials:forms.add.inputs.commercial'),
          type: DataType.SELECT,
          selectOptions: commercialSubjectsOptions,
          required: true,
          isLoading: isFetchingCommercial,
          errorState: invalidCommSubject,
          errorText: t('commercials:forms.add.errors.invalidCommercial'),
          onChange(value, formState) {
            getProductsOptions(+value)
            setCommercial({ ...commercial, commercialSubjectId: +value })
          }
        },
        {
          name: 'productId',
          label: t('commercials:forms.add.inputs.linkedProduct'),
          type: DataType.SELECT,
          selectOptions: productFromcontractProductsListMapper(subjectProductsList),
          isLoading: subjectProductsFetching,
          required: true,
          readOnly: !commercial.commercialSubjectId,
          helpText: t('commercials:forms.add.help.enterCommercial')
        },
        {
          name: 'linkToAllContracts',
          label: t('commercials:forms.add.inputs.linkToAllContracts'),
          type: DataType.SWITCH,
          readOnly: missingContracts || !linkAllContractsAllowed,
          onChange: (value: any) => {
            const active = value as boolean
            setCommercial(prevState => ({
              ...prevState,
              linkToAllContracts: active,
              linkToAllProducts: !active,
              contractProductId: 999,
              contractId: undefined
            }))
          }
        },
        {
          name: 'contractId',
          label: t('commercials:forms.add.inputs.contract'),
          type: DataType.SELECT,
          readOnly: contractsListOptions.length === 0,
          selectOptions: contractsListOptions,
          required: !commercial.linkToAllContracts,
          display: !commercial.linkToAllContracts,
          errorState: missingContracts || contractsListOptions.length === 0,
          errorText: t('commercials:forms.add.help.linkToAllContracts'),
          onChange: (value, formState) => {
            getContractProducts(+value)
            validateActionsForEdit({ ...(formState as CommercialFormModel), contractId: +value })
          }
        },
        {
          name: 'contractProductId',
          label: t('commercials:forms.add.inputs.linkedProducts'),
          type: DataType.SELECT,
          isLoading: contractProductsFetching,
          selectOptions: filterContractProductsOptions(),
          errorState: missingContractProducts,
          errorText: t('commercials:forms.add.errors.productsNotFound'),
          display: !commercial.linkToAllContracts,
          required: !commercial.linkToAllContracts,
          readOnly: commercial.linkToAllProducts || missingContractProducts,
          onChange: (value, formState) => {
            setCommercial(prevState => {
              validateActionsForEdit({
                ...(formState as CommercialFormModel),
                contractProductId: +value
              })
              return {
                ...prevState,
                contractProductId: +value
              }
            })
          }
        },
        {
          name: 'linkToAllProducts',
          label: t('commercials:forms.add.inputs.linkToAllProducts'),
          type: DataType.SWITCH,
          readOnly: missingContractProducts || !linkAllProductsAllowed,
          display: !commercial.linkToAllContracts,
          defaultValue: true,
          onChange: (value: any) => {
            const active = value as boolean
            setCommercial(prevState => ({
              ...prevState,
              linkToAllProducts: active,
              contractProductId: active ? 999 : prevState.contractProductId
            }))
          }
        },
        {
          name: 'errorMsg',
          label: 'errorMsg',
          labelTitle: errorMsg,
          type: DataType.LABEL,
          display: missingContracts || errorMsg !== ''
        }
      ]
    }
  ]

  //Form config Ends

  return (
    <Page notification={globalNotification} fullWidth={false} layout={PageLayout.SINGLE_COLUMN}>
      <PageHeader title={t('commercials:title')}></PageHeader>
      {isFetchingClient || isFetchingDeps ? (
        <FormSkeleton withTitle={true} elevation={0} columns={1} inputs={9}></FormSkeleton>
      ) : (
        <>
          <Box mb={1}>
            <Breadcrumbs separator=">" aria-label="breadcrumb">
              {clientData && (
                <BreadcrumbLink toPathName="/client/file" state={{ clientID: clientData?.id }}>
                  {`Cliente ${getLinkClientText(clientData)} - ID ${clientData.documentID}`}
                </BreadcrumbLink>
              )}
              <Typography key="3" color="primary" variant="subtitle1">
                {t('commercials:breadcrumbs.new')}
              </Typography>
            </Breadcrumbs>
          </Box>
          <Paper elevation={6}>
            <Form
              title={t('commercials:forms.add.titles.main')}
              sections={sectionsData}
              data={commercial}
              options={options}
            />
          </Paper>
        </>
      )}
    </Page>
  )
}
